import React, { useEffect, useState } from "react";
import { Ticket } from "lucide-react";

/**
 * PremiereIntro — ceremonial intro overlay that plays once for ticket holders
 * arriving after a successful paid checkout.
 *
 * Lifecycle (driven by the parent setting `show` from false → true):
 *   t=0.0s : full-screen black dim drops in, marquee message fades up
 *   t=0.8s : "TICKET HOLDER · ROW A" pill animates in, name spotlights
 *   t=2.4s : "The show is about to begin" sub-text fades up
 *   t=3.2s : fade out, parent setShow(false) (curtains take over from here)
 *
 * Pure CSS animations + a single useEffect timer.
 *
 * Props:
 *   - show       : boolean — toggle to start/end the intro
 *   - holderName : string  — viewer's display name
 *   - showTitle  : string  — what they paid to watch (optional)
 *   - onComplete : () => void — called when the sequence ends
 */
export default function PremiereIntro({ show, holderName, showTitle, onComplete }) {
  const [visible, setVisible] = useState(show);
  useEffect(() => {
    if (!show) { setVisible(false); return; }
    setVisible(true);
    const t = setTimeout(() => {
      setVisible(false);
      onComplete && onComplete();
    }, 3200);
    return () => clearTimeout(t);
  }, [show, onComplete]);

  if (!visible) return null;

  return (
    <div
      data-testid="premiere-intro"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black overflow-hidden animate-premiere-fade"
      aria-hidden="true"
    >
      {/* Spotlight beam */}
      <div className="absolute inset-0 pointer-events-none"
           style={{
             background:
               "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(246,195,74,0.18) 0%, transparent 70%)",
           }}
      />
      <div className="relative text-center px-6 max-w-2xl">
        {/* Ticket pill */}
        <div className="inline-flex items-center gap-2 bg-[#f6c34a]/15 border border-[#f6c34a]/50 rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.35em] font-bold text-[#f6c34a] mb-6 animate-premiere-pill">
          <Ticket className="w-3.5 h-3.5" />
          Ticket holder · row A · {holderName || "you"}
        </div>

        {/* Spotlight heading */}
        <h1
          className="text-5xl sm:text-7xl md:text-8xl font-black leading-none mb-6 animate-premiere-title"
          style={{
            fontFamily: "'Outfit', sans-serif",
            background: "linear-gradient(180deg, #fff7d6 0%, #f6c34a 50%, #b8770c 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 36px rgba(246, 195, 74, 0.6))",
          }}
          data-testid="premiere-title"
        >
          Welcome to the premiere
        </h1>

        {showTitle && (
          <div className="text-sm sm:text-base text-white/80 font-semibold uppercase tracking-[0.3em] animate-premiere-sub">
            {showTitle}
          </div>
        )}

        <div
          data-testid="premiere-sub"
          className="text-xs uppercase tracking-[0.3em] text-[#f6c34a]/70 mt-8 animate-premiere-sub"
        >
          The show is about to begin
        </div>
      </div>

      <style>{`
        @keyframes premiere-fade {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes premiere-pill {
          0%   { opacity: 0; transform: translateY(-8px); letter-spacing: 0.5em; }
          50%  { opacity: 1; transform: translateY(0); letter-spacing: 0.35em; }
          100% { opacity: 1; }
        }
        @keyframes premiere-title {
          0%   { opacity: 0; transform: scale(0.92); filter: blur(8px); }
          60%  { opacity: 1; transform: scale(1); filter: blur(0); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes premiere-sub {
          0%, 60% { opacity: 0; }
          100%    { opacity: 1; }
        }
        .animate-premiere-fade  { animation: premiere-fade  3.2s ease-in-out both; }
        .animate-premiere-pill  { animation: premiere-pill  1.4s ease-out 0.15s both; }
        .animate-premiere-title { animation: premiere-title 1.6s cubic-bezier(0.22, 0.61, 0.36, 1) 0.4s both; }
        .animate-premiere-sub   { animation: premiere-sub   1.6s ease-out 1.8s both; }
      `}</style>
    </div>
  );
}
